"use client";
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';
import { Product, formatPrice } from '@/lib/store';

interface CartItem {
  productId: string;
  quantity: number;
  product: Product;
}

export default function CartPage() {
  const router = useRouter();
  const [cart, setCart] = useState<CartItem[]>([]);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    loadCart();
  }, []);

  const loadCart = () => {
    try {
      const stored = JSON.parse(localStorage.getItem('cakepalace_cart') || '[]');
      setCart(stored);
    } catch { setCart([]); }
  };

  const saveCart = (newCart: CartItem[]) => {
    localStorage.setItem('cakepalace_cart', JSON.stringify(newCart));
    window.dispatchEvent(new Event('cartUpdated'));
    setCart(newCart);
  };

  const updateQty = (productId: string, delta: number) => {
    const updated = cart.map(item => {
      if (item.productId !== productId) return item;
      const newQty = Math.max(1, Math.min(item.product.stock, item.quantity + delta));
      return { ...item, quantity: newQty };
    });
    saveCart(updated);
  };

  const removeItem = (productId: string) => {
    saveCart(cart.filter(item => item.productId !== productId));
  };

  const subtotal = cart.reduce((sum, item) => sum + item.product.price * item.quantity, 0);
  const deliveryFee = subtotal >= 200 ? 0 : 30;
  const total = subtotal + deliveryFee;

  if (!mounted) return null;

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />

      <main className="flex-1 max-w-container mx-auto px-4 sm:px-6 py-8 sm:py-12 w-full">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <h1 className="font-display text-3xl sm:text-4xl font-semibold text-foreground">
            Your Cart
          </h1>
          {cart.length > 0 && (
            <span className="text-sm text-muted">{cart.reduce((s, i) => s + i.quantity, 0)} items</span>
          )}
        </div>

        {cart.length === 0 ? (
          /* Empty cart */
          <div className="text-center py-24 animate-fade-in">
            <div className="w-24 h-24 rounded-full bg-accent flex items-center justify-center mx-auto mb-6">
              <Icon name="ShoppingCartIcon" size={40} className="text-primary" />
            </div>
            <h2 className="font-display text-2xl font-semibold text-foreground mb-3">Your cart is empty</h2>
            <p className="text-muted mb-8">Add some products to get started!</p>
            <Link href="/product-shop" className="btn-primary px-8 py-3 text-sm inline-flex items-center gap-2">
              <Icon name="ShoppingBagIcon" size={18} />
              Browse Products
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Cart Items */}
            <div className="lg:col-span-2 space-y-4">
              {cart.map((item, idx) => (
                <div
                  key={item.productId}
                  className="bg-white rounded-md border border-border p-4 sm:p-5 flex gap-4 animate-fade-in-up"
                  style={{ animationDelay: `${idx * 80}ms` }}
                >
                  {/* Image */}
                  <Link href={`/product-detail?id=${item.productId}`} className="flex-shrink-0">
                    <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-sm overflow-hidden bg-accent">
                      <AppImage
                        src={item.product.image}
                        alt={item.product.name}
                        width={96}
                        height={96}
                        className="object-cover w-full h-full hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                  </Link>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between gap-2">
                      <div className="min-w-0">
                        <Link href={`/product-detail?id=${item.productId}`}>
                          <h3 className="font-semibold text-foreground text-sm sm:text-base hover:text-primary transition-colors leading-snug line-clamp-2">
                            {item.product.name}
                          </h3>
                        </Link>
                        <p className="text-xs text-muted mt-0.5">{item.product.category}</p>
                      </div>
                      <button
                        onClick={() => removeItem(item.productId)}
                        className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-sm text-muted-light hover:text-danger hover:bg-red-50 transition-colors"
                        aria-label="Remove item"
                      >
                        <Icon name="TrashIcon" size={16} />
                      </button>
                    </div>

                    <div className="flex items-center justify-between mt-4">
                      {/* Qty controls */}
                      <div className="flex items-center gap-2">
                        <button
                          className="qty-btn"
                          onClick={() => updateQty(item.productId, -1)}
                          disabled={item.quantity <= 1}
                        >
                          <Icon name="MinusIcon" size={14} />
                        </button>
                        <span className="w-8 text-center text-sm font-semibold">{item.quantity}</span>
                        <button
                          className="qty-btn"
                          onClick={() => updateQty(item.productId, 1)}
                          disabled={item.quantity >= item.product.stock}
                        >
                          <Icon name="PlusIcon" size={14} />
                        </button>
                      </div>

                      {/* Price */}
                      <div className="text-right">
                        <p className="font-display font-semibold text-base text-primary">{formatPrice(item.product.price * item.quantity)}</p>
                        {item.quantity > 1 && (
                          <p className="text-xs text-muted">{formatPrice(item.product.price)} each</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}

              {/* Continue shopping */}
              <Link
                href="/product-shop"
                className="flex items-center gap-2 text-sm text-primary font-medium hover:underline pt-2"
              >
                <Icon name="ArrowLeftIcon" size={16} />
                Continue shopping
              </Link>
            </div>

            {/* Order Summary */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-md border border-border p-5 sm:p-6 lg:sticky lg:top-24">
                <h2 className="font-display text-xl font-semibold text-foreground mb-6">Order Summary</h2>

                <div className="space-y-3 mb-6">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted">Subtotal ({cart.reduce((s, i) => s + i.quantity, 0)} items)</span>
                    <span className="font-medium text-foreground">{formatPrice(subtotal)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted">Delivery fee</span>
                    <span className={`font-medium ${deliveryFee === 0 ? 'text-success' : 'text-foreground'}`}>
                      {deliveryFee === 0 ? 'Free' : formatPrice(deliveryFee)}
                    </span>
                  </div>
                  {deliveryFee > 0 && (
                    <p className="text-xs text-muted bg-accent rounded-sm px-3 py-2">
                      Add {formatPrice(200 - subtotal)} more to get free delivery in Accra
                    </p>
                  )}
                  <div className="border-t border-border pt-3">
                    <div className="flex justify-between">
                      <span className="font-semibold text-foreground">Total</span>
                      <span className="font-display font-semibold text-xl text-primary">{formatPrice(total)}</span>
                    </div>
                  </div>
                </div>

                <button
                  onClick={() => router.push('/checkout')}
                  className="btn-primary w-full py-3.5 text-sm flex items-center justify-center gap-2"
                >
                  Proceed to Checkout
                  <Icon name="ArrowRightIcon" size={16} />
                </button>

                <div className="mt-4 flex items-center justify-center gap-2 text-xs text-muted">
                  <Icon name="LockClosedIcon" size={14} className="text-muted-light" />
                  Secure checkout
                </div>
              </div>
            </div>
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
}